import { Link, useLocation, useRoute } from "wouter";
import {
  LayoutDashboard,
  FileText,
  Table2,
  Building2,
  Users,
  Lightbulb,
  PenTool,
  FolderCheck,
  ShieldCheck,
  MessageSquare,
  Plus,
  ChevronRight,
  Settings,
  LogOut,
  UserSquare2,
} from "lucide-react";
import { useClerk, useUser } from "@clerk/clerk-react";
import { cn } from "@/lib/utils";

interface NavItem {
  label: string;
  /** Path relative to a project root (e.g. "/summary") or absolute ("/"). */
  path: string;
  icon: React.ElementType;
  /** When true, only show when a projectId is present. */
  projectScoped?: boolean;
}

const navItems: NavItem[] = [
  { label: "Dashboard", path: "/", icon: LayoutDashboard },
  { label: "Summary", path: "/summary", icon: FileText, projectScoped: true },
  { label: "About Us", path: "/about-us", icon: Building2, projectScoped: true },
  { label: "About Client", path: "/about-client", icon: Users, projectScoped: true },
  { label: "Strategy", path: "/strategy", icon: Lightbulb, projectScoped: true },
  { label: "Requirements Tracker", path: "/matrix", icon: Table2, projectScoped: true },
  { label: "RFP Response", path: "/drafts", icon: PenTool, projectScoped: true },
  { label: "Attachments", path: "/attachments", icon: FolderCheck, projectScoped: true },
  { label: "Final Check", path: "/qa", icon: ShieldCheck, projectScoped: true },
  { label: "Updates", path: "/updates", icon: MessageSquare, projectScoped: true },
];

interface LayoutProps {
  children: React.ReactNode;
  projectTitle?: string;
  /** When set, project-scoped nav items resolve to /projects/:id/<path>. */
  projectId?: string;
}

export default function Layout({ children, projectTitle, projectId }: LayoutProps) {
  const [location] = useLocation();
  const { signOut } = useClerk();
  const { user } = useUser();

  // Auto-derive projectId from the URL if a project page didn't pass one
  // explicitly. Matches /projects/:id and any deeper /projects/:id/...
  const [, projectParams] = useRoute<{ id: string }>("/projects/:id/:rest*");
  const [, projectRoot] = useRoute<{ id: string }>("/projects/:id");
  const effectiveProjectId = projectId ?? projectParams?.id ?? projectRoot?.id;

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: "#0B0B0C" }}>
      <aside
        className="w-64 flex-shrink-0 flex flex-col border-r"
        style={{ backgroundColor: "#111318", borderColor: "rgba(229,231,235,0.08)" }}
      >
        <div className="px-6 py-5 border-b" style={{ borderColor: "rgba(229,231,235,0.08)" }}>
          <div className="flex items-center gap-2">
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold text-white"
              style={{ backgroundColor: "#F97316" }}
            >
              C
            </div>
            <span className="text-white font-semibold text-sm tracking-tight">Callaloo RFP</span>
          </div>
          {projectTitle && (
            <div className="mt-3 text-xs font-medium truncate" style={{ color: "#6B7280" }}>
              {projectTitle}
            </div>
          )}
        </div>

        <div className="px-4 pt-4">
          <Link href="/new">
            <button
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold text-white transition-all"
              style={{ backgroundColor: "#F97316" }}
            >
              <Plus size={14} />
              New RFP
            </button>
          </Link>
        </div>

        <nav className="flex-1 px-3 pt-4 pb-4 flex flex-col gap-0.5">
          {navItems.map((item) => {
            if (item.projectScoped && !effectiveProjectId) return null;
            const Icon = item.icon;
            const href =
              item.projectScoped && effectiveProjectId
                ? `/projects/${effectiveProjectId}${item.path}`
                : item.path;
            const isActive = location === href;
            return (
              <Link key={item.label} href={href}>
                <button
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-medium transition-all text-left",
                    isActive ? "text-white" : "text-gray-400 hover:text-white hover:bg-white/5",
                  )}
                  style={
                    isActive ? { backgroundColor: "rgba(249,115,22,0.15)", color: "#F97316" } : {}
                  }
                >
                  <Icon size={15} />
                  {item.label}
                  {isActive && <ChevronRight size={12} className="ml-auto opacity-60" />}
                </button>
              </Link>
            );
          })}
        </nav>

        {/* User strip */}
        <div
          className="px-4 py-3 border-t flex items-center justify-between gap-2"
          style={{ borderColor: "rgba(229,231,235,0.08)" }}
        >
          <div className="min-w-0">
            <div className="text-xs font-medium text-white truncate">
              {user?.primaryEmailAddress?.emailAddress ?? user?.username ?? "Signed in"}
            </div>
            <div className="text-[10px]" style={{ color: "#6B7280" }}>
              v0.1 · Production build
            </div>
          </div>
          <Link href="/settings/team-profiles">
            <button
              className="text-gray-400 hover:text-white p-1.5 rounded-lg hover:bg-white/5"
              title="Team profiles"
            >
              <UserSquare2 size={14} />
            </button>
          </Link>
          <Link href="/settings/team">
            <button
              className="text-gray-400 hover:text-white p-1.5 rounded-lg hover:bg-white/5"
              title="Team"
            >
              <Users size={14} />
            </button>
          </Link>
          <Link href="/settings/integrations">
            <button
              className="text-gray-400 hover:text-white p-1.5 rounded-lg hover:bg-white/5"
              title="Integrations & settings"
            >
              <Settings size={14} />
            </button>
          </Link>
          <button
            onClick={() => signOut()}
            className="text-gray-400 hover:text-white p-1.5 rounded-lg hover:bg-white/5"
            title="Sign out"
          >
            <LogOut size={14} />
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">{children}</main>
    </div>
  );
}

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}

export function PageHeader({ title, subtitle, actions }: PageHeaderProps) {
  return (
    <div
      className="flex items-start justify-between px-8 py-6 border-b"
      style={{ borderColor: "rgba(229,231,235,0.08)" }}
    >
      <div>
        <h1 className="text-xl font-bold text-white">{title}</h1>
        {subtitle && (
          <p className="mt-1 text-sm" style={{ color: "#6B7280" }}>
            {subtitle}
          </p>
        )}
      </div>
      {actions && <div className="flex items-center gap-3">{actions}</div>}
    </div>
  );
}
